import { faChevronRight } from "@fortawesome/free-solid-svg-icons";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons/faChevronLeft";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  addHours,
  addMonths,
  eachDayOfInterval,
  formatISO,
  getYear,
  isAfter,
  isBefore,
  isWithinInterval,
  startOfDay,
  subDays,
} from "date-fns";
import { subMonths } from "date-fns/subMonths";
import { Calendar } from "heimv-calendar";
import { type Dispatch, type SetStateAction, useCallback, useMemo, useState } from "react";
import Button from "react-bootstrap/esm/Button";
import "heimv-calendar/index.css";
import { useQuery } from "@apollo/client/react";
import Spinner from "react-bootstrap/esm/Spinner";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "../services/i18n";
import { GetOccupanciesDocument, type OccupancyDataFragment, type OccupancyType } from "../types/graphql/graphql";

const calendarColors: Record<OccupancyType, string | undefined> = {
  FREE: undefined,
  TENTATIVE: "#e8bc56",
  OCCUPIED: "#e85f5f",
  CLOSED: "#888888",
};

function slotColor(currentColor: string | undefined, occupancyType: OccupancyType | undefined) {
  if (!occupancyType) return currentColor;
  if (!currentColor) return calendarColors[occupancyType];
  if (Object.keys(calendarColors).indexOf(occupancyType) > Object.keys(calendarColors).indexOf(currentColor))
    return calendarColors[occupancyType];
  return currentColor;
}

type OccupancySlot<T> = {
  allDay?: { key: string; data?: T; color?: string };
  forenoon?: { key: string; data?: T; color?: string };
  afternoon?: { key: string; data?: T; color?: string };
};

type Props = {
  campsiteId: number;
};

type DateIndexedOccupancies = Map<string, OccupancyDataFragment[]>;

function indexOccupanciesByDate(occupancies: OccupancyDataFragment[] | undefined | null): DateIndexedOccupancies {
  const map = new Map<string, OccupancyDataFragment[]>();
  if (!occupancies) return map;

  for (const occupancy of occupancies) {
    if (!occupancy.beginsAt || !occupancy.endsAt) continue;
    for (const date of eachDayOfInterval({ start: occupancy.beginsAt, end: occupancy.endsAt })) {
      const isoDate = formatISO(date, { representation: "date" });
      if (!map.has(isoDate)) map.set(isoDate, []);
      map.get(isoDate)?.push(occupancy);
    }
  }
  return map;
}

export default function CampsiteCalendar({ campsiteId }: Props) {
  const [firstDate, setFirstDate] = useState(new Date());
  const yearFrom = getYear(firstDate);
  const yearTo = getYear(addMonths(firstDate, 10));
  const { data, loading } = useQuery(GetOccupanciesDocument, { variables: { campsiteId } });
  //   // biome-ignore lint/style/noNonNullAssertion: <explanation>
  //   variables: { id: expandedCard! },
  //   skip: !expandedCard,
  // });
  const occupancies = data?.campsite?.publicOccupancies;
  const seasons = data?.campsite?.upcomingSeasons;
  const calendarProvider = data?.campsite?.calendarProvider;
  const reservationUrl = data?.campsite?.reservationUrl;
  const today = new Date();
  const { t } = useTranslation();
  const indexedOccupancies = useMemo(() => indexOccupanciesByDate(occupancies), [occupancies]);
  const getOccupancyOfDate = useCallback(
    (date: Date) => {
      const isoDate = formatISO(date, { representation: "date" });
      const occupanciesOfDate = indexedOccupancies.get(isoDate) || [];
      if (occupanciesOfDate.length === 0) return;
      const slot: OccupancySlot<{ occupancies: OccupancyDataFragment[] }> = {};
      const dayStart = startOfDay(date);
      const dayMid = addHours(dayStart, 12);

      for (const occupancy of occupanciesOfDate) {
        const { beginsAt, endsAt } = occupancy;
        if (!beginsAt || !endsAt) continue;
        const beginsBeforeDayMid = isBefore(beginsAt, dayMid);
        const endsAfterDayStart = isAfter(endsAt, dayStart);
        const endsAfterDayMid = isAfter(endsAt, dayMid);

        if (beginsBeforeDayMid && endsAfterDayMid) {
          if (slot.allDay === undefined) slot.allDay = { key: occupancy.id, data: { occupancies: [] } };
          slot.allDay.color = slotColor(slot.allDay.color, occupancy.occupancyType || undefined);
          slot.allDay.data?.occupancies?.push(occupancy);
          continue;
        }
        if (beginsBeforeDayMid && endsAfterDayStart) {
          if (slot.forenoon === undefined) {
            slot.forenoon = { key: occupancy.id, data: { occupancies: [] } };
          }
          slot.forenoon.color = slotColor(slot.forenoon.color, occupancy.occupancyType || undefined);
          slot.forenoon.data?.occupancies?.push(occupancy);
          continue;
        }
        if (!beginsBeforeDayMid && endsAfterDayMid) {
          if (slot.afternoon === undefined) {
            slot.afternoon = { key: occupancy.id, data: { occupancies: [] } };
          }
          slot.afternoon.color = slotColor(slot.afternoon.color, occupancy.occupancyType || undefined);
          slot.afternoon.data?.occupancies?.push(occupancy);
        }
      }
      return slot;
    },
    [indexedOccupancies],
  );
  const disableDate = useCallback(
    (date: Date) => {
      if (isBefore(date, subDays(today, 1))) return true;
      if (
        seasons &&
        (!calendarProvider || calendarProvider.considerSeasons) &&
        !seasons.some(
          (season) =>
            season.beginsAt && season.endsAt && isWithinInterval(date, { start: season.beginsAt, end: season.endsAt }),
        )
      )
        return true;
      return false;
    },
    [today, seasons, calendarProvider],
  );
  const getDateHref = useCallback(
    (date: Date) => reservationUrl?.replace("__DATE__", formatISO(date, { representation: "date" })) ?? "",
    [reservationUrl],
  );

  if (loading) return <Spinner className="d-block mx-auto my-5" />;

  return (
    <>
      <CalendarNav yearFrom={yearFrom} yearTo={yearTo} setFirstDate={setFirstDate} firstDate={firstDate} />
      <Calendar
        firstDate={firstDate}
        occupancyOfDate={getOccupancyOfDate}
        mode="interactive"
        disableDate={disableDate}
        visibleMonth={8}
        getDateHref={getDateHref}
        hrefTarget="_blank"
      />
      <div className="calendar-legend d-flex justify-content-between small align-items-center text-muted">
        <dl className="d-flex gap-2  align-items-center">
          <dt className="ms-2">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill={calendarColors.TENTATIVE} height="1.5em">
              <rect width="48" height="48" />
            </svg>
          </dt>
          <dd className="m-0"> {t("enums.occupancy.occupancy_types.tentative")}</dd>

          <dt className="ms-2">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill={calendarColors.OCCUPIED} height="1.5em">
              <rect width="48" height="48" />
            </svg>
          </dt>
          <dd className="m-0"> {t("enums.occupancy.occupancy_types.occupied")}</dd>

          <dt className="ms-2">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48" fill={calendarColors.CLOSED} height="1.5em">
              <rect width="48" height="48" />
            </svg>
          </dt>
          <dd className="m-0"> {t("enums.occupancy.occupancy_types.closed")}</dd>
        </dl>
        {calendarProvider?.syncedAt && (
          <p className="text-end">
            {t("public.campsites.tabs.booking.synced_at", { synced_at: formatDateTime(calendarProvider.syncedAt) })}
          </p>
        )}
      </div>
    </>
  );
}

function CalendarNav({
  yearFrom,
  yearTo,
  firstDate,
  setFirstDate,
}: {
  yearFrom: number;
  yearTo: number;
  firstDate: Date;
  setFirstDate: Dispatch<SetStateAction<Date>>;
}) {
  return (
    <nav className="d-flex justify-content-center gap-3 align-items-center">
      <Button variant="link" onClick={() => setFirstDate(subMonths(firstDate, 1))}>
        <FontAwesomeIcon size="lg" icon={faChevronLeft} />
      </Button>
      <span className="fs-3 my-2 fw-bold text-center" style={{ minWidth: "6em" }}>
        {yearFrom}
        {yearFrom !== yearTo && ` / ${yearTo}`}
      </span>
      <Button variant="link" onClick={() => setFirstDate(addMonths(firstDate, 1))}>
        <FontAwesomeIcon size="lg" icon={faChevronRight} />
      </Button>
    </nav>
  );
}
